import type { TOC } from '@ember/component/template-only';
import { fn } from '@ember/helper';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert } from '@ember/render-modifiers';
import {
  PrismBanner,
  PrismCard,
  PrismIcon,
  PrismIconFlag,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import sortableGroup from 'ember-sortable/modifiers/sortable-group';
import sortableItem from 'ember-sortable/modifiers/sortable-item';
import GeneralModalsModalSort from 'lumen/components/general/modals/modal-sort';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import SettingsPosCountrySelect from 'lumen/components/settings/pos/country-select';
import type HotelSettingsPosController from 'lumen/controllers/hotel/settings/pos';
import hasFeature from 'lumen/helpers/has-feature';

interface HotelSettingsPosSignature {
  Args: {
    controller: HotelSettingsPosController;
    model: unknown;
  };
}

<template>
  <SettingsPageTemplate
    @description={{t "settings.pos.description"}}
    @hasAccountSelector={{true}}
    @title={{t "settings.pos.title"}}
    @type="container"
  >
    <div class="pos-container">
      <div
        class="flex w-full justify-start"
        {{didInsert @controller.setRsPosCountries}}
      >
        <PrismCard @contentLayout="noPadding" class="card-tile--pos w-full">
          <:content>
            <div class="card-tile__content flex flex-col gap-400 p-400">
              <PrismBanner
                @bannerMessage={{t "settings.pos.confirmation.body"}}
                @icon="circle-information-outline"
                data-test-id="pos-information-banner"
              />

              <span class="paragraph-300--regular">
                {{t
                  "settings.pos.default_pos"
                  name=@controller.rsDefaultPOS.name
                }}
              </span>

              {{#if (hasFeature "rate_strategy.pos.edit")}}
                <GeneralModalsModalSort
                  @footerElement={{@controller.footerElement}}
                  @items={{@controller.rsPosCountries}}
                  @onCancel={{@controller.cancelPosCountries}}
                  @onReorder={{@controller.reorderCountries}}
                  @onSave={{@controller.confirmChangesOnSave}}
                  @saveDisabled={{@controller.isSaveDisabled}}
                  @showName={{false}}
                  as |item|
                >
                  <div
                    class="handle-container
                      {{if item.isHovering 'highlighted'}}"
                  >
                    <div class="handle flex items-center justify-end">
                      <PrismIcon @name="drag" />
                    </div>
                    <div class="highlight-value position truncate">
                      {{item.order}}
                    </div>
                  </div>
                  <div
                    class="action-container flex flex-1 flex-col"
                    data-test-id="pos-item-country"
                  >
                    <SettingsPosCountrySelect
                      @item={{item}}
                      {{! @glint-expect-error: Incorrect type }}
                      @onChange={{fn @controller.setCurrentCountry item}}
                    />
                  </div>
                </GeneralModalsModalSort>
              {{else}}
                <div class="sort-container">
                  <div class="sort-body">
                    {{#let "pos" as |groupName|}}
                      <ul
                        class="list-sort w-full"
                        {{! @glint-expect-error: Incorrect type }}
                        {{sortableGroup groupName=groupName}}
                      >
                        {{#each @controller.rsPosCountries as |item|}}
                          {{#if item.id}}
                            <li {{sortableItem model=item groupName=groupName}}>
                              <div class="flex flex-row">
                                <div class="handle-container">
                                  <div
                                    class="highlight-value position truncate"
                                  >
                                    {{item.order}}
                                  </div>
                                </div>
                                <div class="action-container">
                                  <div class="dropdown-pos flex items-center">
                                    <PrismIconFlag @code={{item.id}} />
                                    <span class="item-name">{{item.name}}</span>
                                  </div>
                                </div>
                              </div>
                            </li>
                          {{/if}}
                        {{/each}}
                      </ul>
                    {{/let}}
                  </div>
                </div>
              {{/if}}
            </div>
          </:content>

          <:footer>
            <div
              class="flex justify-end"
              id="pos-save-container"
              {{didInsert @controller.onInsertFooter}}
            >
              {{! The sortable modal will render the save button in this slot }}
            </div>
          </:footer>
        </PrismCard>
      </div>
    </div>
  </SettingsPageTemplate>
</template> satisfies TOC<HotelSettingsPosSignature>;
